import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useTheme } from '../ThemeContext';
import { agentAuthHeaders, adminSessionFetch } from '../utils/functionAuth';
import { rememberAdminEmailToken, clearAdminEmailToken } from '../utils/adminEmailSession';
import { resetAppScroll } from '../utils/appScroll';
import { showToast } from './Toast';
import {
  BEFORE_YOU_HIRE,
  BeforeYouHireModal,
  VendorCardFootnote,
  postVendorConsent,
  useVendorHireGate,
} from './VendorConsent';
import {
  APPLICATION_FILTERS,
  activitySentence,
  applicationCard,
  applicationFilterSubtitle,
  applicationStatusLabel,
  applicationsForFilter,
  inquiryStatusLabel,
} from '../shared/adminOverview';
import { AGENT_ROLES, adminPageRequiresAal2 } from '../shared/pulseMetrics';
import PulseDashboard from './PulseDashboard';
import DiscountCodesPanel from './DiscountCodesPanel';
import CeoSeat, { CEO_SEAT_TABS } from './ceo/CeoSeat';
import AdminEmailCode from './AdminEmailCode';
import SearchableCategorySelect from './SearchableCategorySelect';
import GlobalTasksAdmin from './GlobalTasksAdmin';
import { vendorCategoryOptions, vendorInCategory } from '../shared/vendorTrades';
import VendorCardCopy from './VendorCardCopy';
import { stripVendorListingDisclaimer } from '../shared/vendorDescription';

let emailChallengeUser = '';

function claimEmailChallenge(userId) {
  const key = String(userId || '');
  if (!key) return true;
  if (emailChallengeUser === key) return false;
  emailChallengeUser = key;
  return true;
}

function releaseEmailChallenge(userId) {
  if (emailChallengeUser === String(userId || '')) emailChallengeUser = '';
}

export function resetAdminEmailChallenge() {
  emailChallengeUser = '';
}

async function defaultAdminEmail2fa(body) {
  const response = await adminSessionFetch('/.netlify/functions/admin-email-2fa', body || {});
  const data = await response.json().catch(() => ({}));
  if (response.status !== 401) rememberAdminEmailToken(data && data.token);
  return {
    ok: response.ok,
    status: response.status,
    required: data.required,
    verified: data.verified,
    sent: data.sent,
    throttled: data.throttled,
    retryAfterSeconds: data.retryAfterSeconds,
    error: data.error || '',
    code: data.code || '',
  };
}

function adminPost(path, body) {
  return adminSessionFetch(path, body);
}

const SuperAdminDashboard = ({ profile, onLogout, previewData, previewSafeTop, email2faApi }) => {
  const { colors } = useTheme();
  const seeded = previewData || null;
  const [activeTab, setActiveTab] = useState((seeded && seeded.tab) || 'pulse');
  const [pulse, setPulse] = useState((seeded && seeded.pulse) || null);
  const [pulseError, setPulseError] = useState('');
  const [mfaBlocked, setMfaBlocked] = useState(false);
  const requireAal2 = adminPageRequiresAal2();
  const [aal, setAal] = useState(seeded ? 'off' : (requireAal2 ? 'pending' : 'off'));
  const [loading, setLoading] = useState(!seeded);
  const [emailGate, setEmailGate] = useState(seeded ? 'open' : 'pending');
  const [emailGateError, setEmailGateError] = useState('');
  const [emailRetryAfter, setEmailRetryAfter] = useState(0);
  const [emailRetryNonce, setEmailRetryNonce] = useState(0);
  const [emailAttempt, setEmailAttempt] = useState(0);
  const [emailBusy, setEmailBusy] = useState(false);
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    resetAppScroll();
  }, [activeTab]);

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (seeded || !requireAal2) return undefined;
    let live = true;
    supabase.auth.mfa.getAuthenticatorAssuranceLevel()
      .then(({ data }) => {
        if (!live) return;
        setAal(data && data.currentLevel === 'aal2' ? 'aal2' : 'aal1');
      })
      .catch(() => {
        if (live) setAal('aal1');
      });
    return () => {
      live = false;
    };
  }, [requireAal2]);

  // Agents list with client counts
  const [agents, setAgents] = useState((seeded && seeded.agents) || []);
  const [agentSearch, setAgentSearch] = useState('');
  const [expandedAgent, setExpandedAgent] = useState(null); // For expandable rows
  const [agentClients, setAgentClients] = useState({}); // Store clients for each agent
  const [editingAgent, setEditingAgent] = useState(null);
  const [showAgentForm, setShowAgentForm] = useState(false);

  // Clients list
  const [clients, setClients] = useState((seeded && seeded.clients) || []);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedAgentFilter, setSelectedAgentFilter] = useState(''); // Filter clients by agent

  // Admin activity logs
  const [activityLogs, setActivityLogs] = useState((seeded && seeded.activityLogs) || []);
  const [logActionFilter, setLogActionFilter] = useState('');
  const [logDateFilter, setLogDateFilter] = useState('');

  // Regional Vendors
  const [vendors, setVendors] = useState((seeded && seeded.vendors) || []);
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorRegionFilter, setVendorRegionFilter] = useState('North Orlando');
  const [vendorCategoryFilter, setVendorCategoryFilter] = useState('');
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [editingVendorId, setEditingVendorId] = useState(null);
  const hireGate = useVendorHireGate(
    async () => {
      const data = await postVendorConsent(await agentAuthHeaders(), { action: 'status' });
      return data.accepted === true;
    },
    async (pending) => {
      await postVendorConsent(await agentAuthHeaders(), {
        action: 'accept',
        consent_type: BEFORE_YOU_HIRE,
        vendor_id: pending && pending.vendorId ? pending.vendorId : null,
      });
    },
  );

  // Selected agent for detailed view
  const [selectedAgent, setSelectedAgent] = useState(null);

  const [applications, setApplications] = useState((seeded && seeded.applications) || []);
  const [applicationFilter, setApplicationFilter] = useState('submitted');
  const [inquiries, setInquiries] = useState((seeded && seeded.inquiries) || []);

  // Agent form state
  const [agentForm, setAgentForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_name: '',
    subscription_plan: 'solo',
    subscription_status: 'active'
  });

  // Vendor form state
  const [vendorForm, setVendorForm] = useState({
    name: '',
    category: 'HVAC',
    phone: '',
    email: '',
    website: '',
    address: '',
    region: 'North Orlando',
    service_areas: [],
    description: '',
    specialties: [],
    license_number: '',
    insurance_verified: false,
    rating: null,
    notes: '',
    is_recommended: false
  });

  useEffect(() => {
    if (seeded) return undefined;
    let live = true;
    const api = email2faApi || defaultAdminEmail2fa;
    (async () => {
      try {
        const status = await api({ action: 'status' });
        if (!live) return;
        if (!status || status.ok === false) {
          setEmailGateError((status && status.error) || 'Could not confirm sign-in');
          setEmailGate('error');
          return;
        }
        if (status.required !== true || status.verified === true) {
          setEmailGate('open');
          return;
        }
        let retryAfter = 0;
        let sendError = '';
        if (claimEmailChallenge(profile && profile.id)) {
          const sent = await api({ action: 'send' });
          if (!live) return;
          if (!sent || (sent.ok === false && !sent.throttled)) {
            releaseEmailChallenge(profile && profile.id);
            sendError = (sent && sent.error) || 'The sign-in code could not be sent';
          }
          retryAfter = sent && sent.retryAfterSeconds ? sent.retryAfterSeconds : 60;
        } else {
          retryAfter = 60;
        }
        if (!live) return;
        setEmailGateError(sendError);
        setEmailRetryNonce((current) => current + 1);
        setEmailRetryAfter(retryAfter);
        setEmailGate('code');
      } catch (gateError) {
        if (!live) return;
        releaseEmailChallenge(profile && profile.id);
        setEmailGateError(gateError.message || 'Could not confirm sign-in');
        setEmailGate('error');
      }
    })();
    return () => {
      live = false;
    };
  }, [seeded, email2faApi, profile, emailAttempt]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (seeded || emailGate !== 'open') return undefined;
    loadData();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailGate]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([
      loadAgents(),
      loadClients(),
      loadVendors(),
      loadActivityLogs(),
      loadApplications(),
      loadInquiries(),
      loadPulse(),
    ]);
    setLoading(false);
  };

  const loadAgents = async () => {
    try {
      // Get all agents first
      const { data: agentsData, error: agentsError } = await supabase
        .from('profiles')
        .select('*')
        .in('role', AGENT_ROLES)
        .order('created_at', { ascending: false });

      if (agentsError) throw agentsError;

      // Get client counts separately
      const agentsWithCounts = await Promise.all(
        (agentsData || []).map(async (agent) => {
          const { count } = await supabase
            .from('clients')
            .select('*', { count: 'exact', head: true })
            .eq('agent_id', agent.id);

          return {
            ...agent,
            client_count: count || 0
          };
        })
      );

      setAgents(agentsWithCounts);
    } catch (error) {
      console.error('Error loading agents:', error);
      setAgents([]);
    }
  };

  const loadAgentClients = async (agentId) => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('agent_id', agentId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setAgentClients(prev => ({
        ...prev,
        [agentId]: data || []
      }));
    } catch (error) {
      console.error('Error loading agent clients:', error);
    }
  };

  const loadClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*, agent:profiles!agent_id(id, first_name, last_name, email)')
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadActivityLogs = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_activity_log')
        .select('*, admin:profiles!admin_id(first_name, last_name, email)')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setActivityLogs(data || []);
    } catch (error) {
      console.error('Error loading activity logs:', error);
    }
  };

  const loadVendors = async () => {
    try {
      const response = await adminPost('/.netlify/functions/regional-vendor-admin', { action: 'list' });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Could not load vendors');
      setVendors(result.vendors || []);
    } catch (error) {
      console.error('Error loading vendors:', error);
      setVendors([]);
    }
  };

  const postAdminQueue = async (action) => {
    const response = await adminPost('/.netlify/functions/admin-queues', { action });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.error || 'Could not load admin data');
    return result;
  };

  const loadApplications = async () => {
    try {
      const result = await postAdminQueue('applications');
      setApplications(result.applications || []);
    } catch (error) {
      console.error('Error loading applications:', error);
      setApplications([]);
    }
  };

  const loadPulse = async () => {
    try {
      const response = await adminPost('/.netlify/functions/admin-pulse', { view: 'pulse' });
      const result = await response.json().catch(() => ({}));
      if (result.code === 'aal2_required') {
        setMfaBlocked(true);
        setPulse(null);
        return;
      }
      if (result.code === 'admin_email_2fa_required') {
        setEmailGate('code');
        setPulse(null);
        return;
      }
      if (!response.ok) throw new Error(result.error || 'Could not load Pulse');
      setPulse(result.pulse || null);
      setPulseError('');
    } catch (loadError) {
      console.error('Error loading Pulse:', loadError);
      setPulseError(loadError.message || 'Could not load Pulse');
    }
  };

  const loadInquiries = async () => {
    try {
      const result = await postAdminQueue('inquiries');
      setInquiries(result.inquiries || []);
    } catch (error) {
      console.error('Error loading inquiries:', error);
      setInquiries([]);
    }
  };

  const logAdminAction = async (actionType, targetType = null, targetId = null, details = null) => {
    try {
      const { error } = await supabase.rpc('log_admin_action', {
        p_action_type: actionType,
        p_target_type: targetType,
        p_target_id: targetId,
        p_details: details
      });

      if (error) throw error;
      await loadActivityLogs(); // Refresh logs
    } catch (error) {
      console.error('Error logging admin action:', error);
    }
  };

  const handleUpdateSubscription = async (agentId, updates) => {
    try {
      const response = await adminPost('/.netlify/functions/profile-entitlements', {
        action: 'admin-update',
        profileId: agentId,
        updates,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Update failed');

      await logAdminAction('update_subscription', 'agent', agentId, updates);
      await loadAgents();
      showToast('Subscription updated successfully!');
      setSelectedAgent(null);
    } catch (error) {
      console.error('Error updating subscription:', error);
      showToast('Error updating subscription: ' + error.message);
    }
  };

  const handleExpandAgent = async (agent) => {
    if (expandedAgent === agent.id) {
      setExpandedAgent(null);
    } else {
      setExpandedAgent(agent.id);
      if (!agentClients[agent.id]) {
        await loadAgentClients(agent.id);
      }
    }
  };

  const handleEditAgent = (agent) => {
    setEditingAgent(agent);
    setAgentForm({
      first_name: agent.first_name || '',
      last_name: agent.last_name || '',
      email: agent.email || '',
      phone: agent.phone || '',
      company_name: agent.company_name || '',
      subscription_plan: agent.subscription_plan || 'solo',
      subscription_status: agent.subscription_status || 'active'
    });
    setShowAgentForm(true);
  };

  const handleDeleteAgent = async (agent) => {
    if (!window.confirm(`Are you sure you want to delete agent "${agent.first_name} ${agent.last_name}"? This will also delete all their clients and data. This action cannot be undone.`)) {
      return;
    }
    try {
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', agent.id);

      if (error) throw error;

      await logAdminAction('delete_agent', 'agent', agent.id, {
        name: `${agent.first_name} ${agent.last_name}`,
        email: agent.email
      });

      await loadAgents();
      showToast('Agent deleted successfully');
    } catch (error) {
      console.error('Error deleting agent:', error);
      showToast('Failed to delete agent. They may have associated data that must be removed first.');
    }
  };

  const handleSaveAgent = async (e) => {
    e.preventDefault();
    try {
      if (!editingAgent) return;

      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: agentForm.first_name,
          last_name: agentForm.last_name,
          email: agentForm.email,
          phone: agentForm.phone,
          company_name: agentForm.company_name,
        })
        .eq('id', editingAgent.id);

      if (error) throw error;

      const response = await adminPost('/.netlify/functions/profile-entitlements', {
        action: 'admin-update',
        profileId: editingAgent.id,
        updates: {
          subscription_plan: agentForm.subscription_plan,
          subscription_status: agentForm.subscription_status,
        },
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Update failed');

      await logAdminAction('update_agent', 'agent', editingAgent.id, {
        name: `${agentForm.first_name} ${agentForm.last_name}`
      });

      await loadAgents();
      setShowAgentForm(false);
      setEditingAgent(null);
      showToast('Agent updated successfully!');
    } catch (error) {
      console.error('Error saving agent:', error);
      showToast('Failed to update agent: ' + error.message);
    }
  };

  const postRegionalVendor = async (body) => {
    const response = await adminPost('/.netlify/functions/regional-vendor-admin', body);
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.error || 'Vendor update failed');
    return result;
  };

  const startEditVendor = (vendor) => {
    setEditingVendorId(vendor.id);
    setVendorForm({
      name: vendor.name || '',
      category: vendor.category || 'HVAC',
      phone: vendor.phone || '',
      email: vendor.email || '',
      website: vendor.website || '',
      address: vendor.address || '',
      region: vendor.region || '',
      service_areas: vendor.service_areas || [],
      description: stripVendorListingDisclaimer(vendor.description || ''),
      specialties: vendor.specialties || [],
      license_number: vendor.license_number || '',
      insurance_verified: vendor.insurance_verified === true,
      rating: vendor.rating == null ? null : vendor.rating,
      notes: vendor.notes || '',
      is_recommended: vendor.is_recommended === true,
    });
    setShowVendorForm(true);
  };

  useEffect(() => {
    if (!(seeded && seeded.openVendor) || !vendors[0]) return undefined;
    startEditVendor(vendors[0]);
    return undefined;
  }, [seeded, vendors]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveVendor = async (e) => {
    e.preventDefault();
    if (!editingVendorId) return;
    try {
      await postRegionalVendor({ action: 'update', id: editingVendorId, vendor: vendorForm });
      await logAdminAction('update_vendor', 'vendor', editingVendorId, { name: vendorForm.name, region: vendorForm.region });
      await loadVendors();
      setShowVendorForm(false);
      setEditingVendorId(null);
      showToast('Vendor updated.');
    } catch (error) {
      console.error('Error updating vendor:', error);
      showToast('Error updating vendor: ' + error.message);
    }
  };

  const handleUpdateVendor = async (vendorId, updates) => {
    try {
      await postRegionalVendor({ action: 'update', id: vendorId, vendor: updates });

      await logAdminAction('update_vendor', 'vendor', vendorId, updates);
      await loadVendors();
      showToast('Vendor updated successfully!');
    } catch (error) {
      console.error('Error updating vendor:', error);
      showToast('Error updating vendor: ' + error.message);
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;

    try {
      await postRegionalVendor({ action: 'delete', id: vendorId });

      await logAdminAction('delete_vendor', 'vendor', vendorId);
      await loadVendors();
      showToast('Vendor deleted successfully!');
    } catch (error) {
      console.error('Error deleting vendor:', error);
      showToast('Error deleting vendor: ' + error.message);
    }
  };

  const handleToggleVendorActive = async (vendor) => {
    await handleUpdateVendor(vendor.id, { is_active: !vendor.is_active });
  };

  // Filters
  const filteredAgents = agents.filter(agent =>
    `${agent.first_name} ${agent.last_name} ${agent.email}`.toLowerCase().includes(agentSearch.toLowerCase())
  );

  const filteredClients = clients.filter(client => {
    const matchesSearch = `${client.first_name} ${client.last_name} ${client.email}`.toLowerCase().includes(clientSearch.toLowerCase());
    const matchesAgent = !selectedAgentFilter || client.agent_id === selectedAgentFilter;
    return matchesSearch && matchesAgent;
  });

  const filteredActivityLogs = activityLogs.filter(log => {
    const needle = logActionFilter.toLowerCase();
    const matchesAction = !logActionFilter
      || String(log.action_type || '').toLowerCase().includes(needle)
      || activitySentence(log).toLowerCase().includes(needle);
    const matchesDate = !logDateFilter || new Date(log.created_at).toLocaleDateString().includes(logDateFilter);
    return matchesAction && matchesDate;
  });

  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch = vendor.name.toLowerCase().includes(vendorSearch.toLowerCase());
    const matchesRegion = !vendorRegionFilter || vendor.region === vendorRegionFilter;
    const matchesCategory = vendorInCategory(vendor, vendorCategoryFilter);
    return matchesSearch && matchesRegion && matchesCategory;
  });

  // Get unique agents for filter dropdown
  const uniqueAgents = Array.from(new Map(clients.map(c => c.agent).filter(Boolean).map(a => [a.id, a])).values());
  const openPulseRow = (row) => {
    if (!row) return;
    if (row.targetType === 'agent' && row.targetId) {
      const agent = agents.find((item) => item.id === row.targetId);
      setAgentSearch('');
      setActiveTab('agents');
      if (agent) setSelectedAgent(agent);
      return;
    }
    if (row.targetType === 'client') {
      setSelectedAgentFilter('');
      setClientSearch(row.email || row.name || '');
      setActiveTab('clients');
      return;
    }
    if (row.targetType === 'inquiry') setActiveTab('inquiries');
  };

  const exportPulse = async (csv) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'pulse-activity.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    try {
      await adminPost('/.netlify/functions/admin-pulse', { view: 'export_activity', logOnly: true });
    } catch (exportError) {
      console.error('Pulse export log failed', exportError);
    }
  };

  const askEmailCode = email2faApi || defaultAdminEmail2fa;
  const leaveAdmin = async () => {
    try {
      await askEmailCode({ action: 'clear' });
    } catch (leaveError) {
      console.error('Admin sign-in session clear failed', leaveError);
    }
    clearAdminEmailToken();
    releaseEmailChallenge(profile && profile.id);
    if (onLogout) onLogout();
  };
  const submitEmailCode = async (code) => {
    setEmailBusy(true);
    setEmailGateError('');
    try {
      const result = await askEmailCode({ action: 'verify', code });
      if (!result || result.verified !== true) {
        setEmailGateError((result && result.error) || 'That code is not valid.');
        setEmailBusy(false);
        return;
      }
      setEmailBusy(false);
      setEmailGate('open');
    } catch (verifyError) {
      setEmailGateError(verifyError.message || 'That code is not valid.');
      setEmailBusy(false);
    }
  };
  const resendEmailCode = async () => {
    if (emailBusy) return;
    setEmailBusy(true);
    setEmailGateError('');
    try {
      const sent = await askEmailCode({ action: 'send' });
      if (!sent || (sent.ok === false && !sent.throttled)) {
        setEmailGateError((sent && sent.error) || 'The sign-in code could not be sent');
      }
      setEmailRetryNonce((current) => current + 1);
      setEmailRetryAfter((sent && sent.retryAfterSeconds) || 60);
    } catch (resendError) {
      setEmailGateError(resendError.message || 'The sign-in code could not be sent');
    }
    setEmailBusy(false);
  };

  const safeTop = previewSafeTop || 'env(safe-area-inset-top, 0px)';
  const pageStyle = {
    ...styles.container,
    background: colors.background,
    paddingTop: `calc(${safeTop} + ${narrow ? '0.75rem' : '1.25rem'})`,
    paddingRight: narrow ? 'max(1rem, env(safe-area-inset-right, 0px))' : '2rem',
    paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))',
    paddingLeft: narrow ? 'max(1rem, env(safe-area-inset-left, 0px))' : '2rem',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  };

  if (!seeded && emailGate === 'pending') {
    return (
      <div className="app-scroll" style={pageStyle}>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ ...styles.loader, borderTopColor: '#59c4c4' }}></div>
        </div>
      </div>
    );
  }

  if (!seeded && emailGate === 'error') {
    return (
      <div className="app-scroll" style={pageStyle}>
        <h1 style={{ ...styles.title, color: colors.text, fontSize: narrow ? '1.75rem' : '2rem' }}>Sign-in</h1>
        <p style={{ color: colors.textSecondary, maxWidth: '24rem', fontSize: '1.0625rem', lineHeight: 1.45 }}>
          {emailGateError || 'Could not confirm sign-in'}
        </p>
        <button
          type="button"
          onClick={() => {
            releaseEmailChallenge(profile && profile.id);
            setEmailGateError('');
            setEmailGate('pending');
            setEmailAttempt((current) => current + 1);
          }}
          style={{ ...styles.logoutBtn, color: colors.text, border: `1px solid ${colors.border}`, minHeight: '3rem' }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!seeded && emailGate === 'code') {
    return (
      <AdminEmailCode
        email={profile && profile.email}
        colors={colors}
        error={emailGateError}
        busy={emailBusy}
        retryAfterSeconds={emailRetryAfter}
        retryNonce={emailRetryNonce}
        onSubmit={submitEmailCode}
        onResend={resendEmailCode}
        onLogout={leaveAdmin}
        previewSafeTop={previewSafeTop}
      />
    );
  }

  if (loading || aal === 'pending') {
    return (
      <div className="app-scroll" style={pageStyle}>
        <div style={{textAlign: 'center', padding: '3rem'}}>
          <div style={{...styles.loader, borderTopColor: '#59c4c4'}}></div>
          <p style={{color: colors.text, marginTop: '1rem'}}>Loading platform data...</p>
        </div>
      </div>
    );
  }

  if (mfaBlocked || aal === 'aal1') {
    return (
      <div className="app-scroll" style={pageStyle}>
        <h1 style={{ ...styles.title, color: colors.text }}>Two-factor authentication</h1>
        <p style={{ color: colors.textSecondary, maxWidth: '28rem' }}>
          This page stays locked until this sign-in uses an authenticator app.
        </p>
        <button onClick={onLogout} style={styles.logoutBtn}>Logout</button>
      </div>
    );
  }

  return (
    <div className="app-scroll" style={pageStyle}>
      {/* Header */}
      <div style={{...styles.header, flexDirection: narrow ? 'column' : 'row', alignItems: narrow ? 'stretch' : 'center', gap: narrow ? '0.75rem' : '1rem', borderBottom: `1px solid ${colors.border}`}}>
        <div style={{minWidth: 0}}>
          <h1 style={{...styles.title, color: colors.text, fontSize: narrow ? '1.35rem' : '1.75rem'}}>
            Super Admin
          </h1>
          <p style={{...styles.subtitle, color: colors.textSecondary}}>
            Pacarion
          </p>
        </div>
        <div style={{...styles.headerActions, justifyContent: narrow ? 'flex-start' : 'flex-end'}}>
          <span style={{...styles.adminBadge, color: colors.text, border: `1px solid ${colors.border}`}}>
            Super Admin
          </span>
          <button onClick={onLogout} style={{...styles.logoutBtn, color: colors.text, border: `1px solid ${colors.border}`}}>
            Log out
          </button>
        </div>
      </div>

      <div style={styles.tabBar} role="tablist" aria-label="Super Admin">
        {[
          ['pulse', 'Pulse'],
          ['agents', `Agents (${agents.length})`],
          ['clients', `Clients (${clients.length})`],
          ['vendors', `Vendors (${vendors.length})`],
          ['tasks', 'Pacarion Starter Tasks'],
          ['discounts', 'Discount codes'],
          ['applications', `Applications (${applications.length})`],
          ['inquiries', `Inquiries (${inquiries.length})`],
          ['activity', 'Activity'],
        ].concat(CEO_SEAT_TABS.map((tab) => [tab.id, tab.label])).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={activeTab === id}
            style={{
              ...styles.tab,
              color: activeTab === id ? colors.background : colors.textSecondary,
              background: activeTab === id ? colors.text : 'transparent',
              borderColor: activeTab === id ? colors.text : colors.border,
            }}
            onClick={() => setActiveTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'pulse' && (
        <div style={styles.content}>
          <PulseDashboard
            colors={colors}
            narrow={narrow}
            pulse={pulse}
            error={pulseError}
            onOpen={openPulseRow}
            onExport={exportPulse}
          />
        </div>
      )}

      {/* Agents Tab */}
      {activeTab === 'agents' && (
        <div style={styles.content}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
            <h2 style={{...styles.sectionTitle, color: colors.text, margin: 0}}>All Agents</h2>
          </div>

          <input
            type="text"
            placeholder="Search agents by name or email..."
            value={agentSearch}
            onChange={(e) => setAgentSearch(e.target.value)}
            style={{...styles.searchInput, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
          />

          {narrow ? (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
              {filteredAgents.map(agent => (
                <div
                  key={agent.id}
                  style={{
                    background: colors.surface,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '0.75rem',
                    padding: '1rem',
                  }}
                >
                  <div style={{fontWeight: '600', color: colors.text}}>{agent.first_name} {agent.last_name}</div>
                  <div style={{fontSize: '0.85rem', color: colors.textSecondary, marginBottom: '0.75rem'}}>{agent.email}</div>
                  <div style={{display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.75rem'}}>
                    <span style={{...styles.badge, background: agent.subscription_plan === 'team' ? '#10b981' : '#59c4c4'}}>
                      {agent.subscription_plan || 'none'}
                    </span>
                    <span style={{
                      ...styles.badge,
                      background: agent.subscription_status === 'active' ? '#10b981' :
                                 agent.subscription_status === 'trialing' ? '#f59e0b' : '#6b7280'
                    }}>
                      {agent.subscription_status || 'none'}
                    </span>
                    <span style={{fontSize: '0.85rem', color: colors.text}}>{agent.client_count} clients</span>
                  </div>
                  <div style={{fontSize: '0.85rem', color: colors.textMuted, marginBottom: '0.75rem'}}>
                    Last active {agent.last_login ? new Date(agent.last_login).toLocaleDateString() : 'never'}
                  </div>
                  <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
                    <button onClick={() => handleExpandAgent(agent)} style={{...styles.actionBtn, background: '#3b82f6'}}>
                      {expandedAgent === agent.id ? 'Hide clients' : 'Clients'}
                    </button>
                    <button onClick={() => handleEditAgent(agent)} style={{...styles.actionBtn, background: '#10b981'}}>Edit</button>
                    <button onClick={() => handleDeleteAgent(agent)} style={{...styles.actionBtn, background: '#ef4444'}}>Delete</button>
                  </div>
                  {expandedAgent === agent.id && (
                    <div style={{marginTop: '0.75rem'}}>
                      {(agentClients[agent.id] || []).length === 0 ? (
                        <p style={{color: colors.textSecondary, fontStyle: 'italic', margin: 0}}>No clients yet</p>
                      ) : (agentClients[agent.id] || []).map(client => (
                        <div key={client.id} style={{padding: '0.5rem 0', borderTop: `1px solid ${colors.borderLight}`, color: colors.text}}>
                          {client.first_name} {client.last_name}
                          <div style={{fontSize: '0.8rem', color: colors.textSecondary}}>{client.email}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
          <div style={{...styles.tableContainer, background: colors.surface, border: `1px solid ${colors.border}`}}>
            <table style={{width: '100%'}}>
              <thead>
                <tr style={{borderBottom: `2px solid ${colors.border}`}}>
                  <th style={{...styles.th, color: colors.text}}>Agent</th>
                  <th style={{...styles.th, color: colors.text}}>Plan</th>
                  <th style={{...styles.th, color: colors.text}}>Status</th>
                  <th style={{...styles.th, color: colors.text}}>Clients</th>
                  <th style={{...styles.th, color: colors.text}}>Last Active</th>
                  <th style={{...styles.th, color: colors.text}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAgents.map(agent => (
                  <React.Fragment key={agent.id}>
                    <tr
                      style={{
                        borderBottom: `1px solid ${colors.borderLight}`,
                        background: expandedAgent === agent.id ? colors.surfaceAlt : 'transparent'
                      }}
                    >
                      <td style={{...styles.td, color: colors.text}}>
                        <div style={{fontWeight: '600'}}>{agent.first_name} {agent.last_name}</div>
                        <div style={{fontSize: '0.85rem', color: colors.textSecondary}}>{agent.email}</div>
                      </td>
                      <td style={{...styles.td, color: colors.text}}>
                        <span style={{
                          ...styles.badge,
                          background: agent.subscription_plan === 'team' ? '#10b981' : '#59c4c4'
                        }}>
                          {agent.subscription_plan || 'none'}
                        </span>
                      </td>
                      <td style={{...styles.td, color: colors.text}}>
                        <span style={{
                          ...styles.badge,
                          background: agent.subscription_status === 'active' ? '#10b981' :
                                     agent.subscription_status === 'trialing' ? '#f59e0b' : '#6b7280'
                        }}>
                          {agent.subscription_status || 'none'}
                        </span>
                      </td>
                      <td style={{...styles.td, color: colors.text}}>
                        <span style={{fontWeight: '600', fontSize: '1.1rem'}}>{agent.client_count}</span>
                      </td>
                      <td style={{...styles.td, color: colors.textMuted, fontSize: '0.85rem'}}>
                        {agent.last_login ? new Date(agent.last_login).toLocaleDateString() : 'Never'}
                      </td>
                      <td style={{...styles.td}}>
                        <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
                          <button
                            onClick={() => handleExpandAgent(agent)}
                            style={{...styles.actionBtn, background: '#3b82f6'}}
                          >
                            {expandedAgent === agent.id ? 'Hide' : 'Clients'}
                          </button>
                          <button
                            onClick={() => handleEditAgent(agent)}
                            style={{...styles.actionBtn, background: '#10b981'}}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteAgent(agent)}
                            style={{...styles.actionBtn, background: '#ef4444'}}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expandable Row - Show Agent's Clients */}
                    {expandedAgent === agent.id && (
                      <tr>
                        <td colSpan="6" style={{padding: '1rem', background: colors.surfaceAlt}}>
                          <h4 style={{color: colors.text, marginBottom: '1rem'}}>
                            Clients for {agent.first_name} {agent.last_name} ({agentClients[agent.id]?.length || 0})
                          </h4>
                          {agentClients[agent.id] && agentClients[agent.id].length > 0 ? (
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '0.75rem'}}>
                              {agentClients[agent.id].map(client => (
                                <div
                                  key={client.id}
                                  style={{
                                    padding: '0.75rem',
                                    background: colors.surface,
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: '0.5rem'
                                  }}
                                >
                                  <div style={{fontWeight: '600', color: colors.text}}>
                                    {client.first_name} {client.last_name}
                                  </div>
                                  <div style={{fontSize: '0.85rem', color: colors.textSecondary}}>
                                    {client.email}
                                  </div>
                                  <div style={{marginTop: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
                                    <span style={{
                                      ...styles.badge,
                                      background: client.status === 'active' ? '#10b981' :
                                                 client.status === 'invited' ? '#f59e0b' : '#6b7280',
                                      fontSize: '0.75rem'
                                    }}>
                                      {client.status}
                                    </span>
                                    <span style={{fontSize: '0.75rem', color: colors.textMuted}}>
                                      {client.property_type?.replace('_', ' ')}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p style={{color: colors.textSecondary, fontStyle: 'italic'}}>No clients yet</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

      {/* Clients Tab */}
      {activeTab === 'clients' && (
        <div style={styles.content}>
          <h2 style={{...styles.sectionTitle, color: colors.text}}>All Clients</h2>

          <div style={{display: 'flex', gap: '1rem', marginBottom: '1rem', flexDirection: narrow ? 'column' : 'row'}}>
            <input
              type="text"
              placeholder="Search clients by name or email..."
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              style={{
                ...styles.searchInput,
                flex: 1,
                background: colors.surface,
                border: `2px solid ${colors.borderLight}`,
                color: colors.text,
                marginBottom: 0
              }}
            />
            <select
              value={selectedAgentFilter}
              onChange={(e) => setSelectedAgentFilter(e.target.value)}
              style={{
                ...styles.searchInput,
                width: narrow ? '100%' : '250px',
                background: colors.surface,
                border: `2px solid ${colors.borderLight}`,
                color: colors.text,
                marginBottom: 0
              }}
            >
              <option value="">All Agents</option>
              {uniqueAgents.map(agent => (
                <option key={agent.id} value={agent.id}>
                  {agent.first_name} {agent.last_name}
                </option>
              ))}
            </select>
          </div>

          {narrow ? (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
              {filteredClients.map(client => (
                <div
                  key={client.id}
                  style={{
                    background: colors.surface,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '0.75rem',
                    padding: '1rem',
                  }}
                >
                  <div style={{fontWeight: '600', color: colors.text}}>{client.first_name} {client.last_name}</div>
                  <div style={{fontSize: '0.85rem', color: colors.textSecondary}}>{client.email}</div>
                  <div style={{fontSize: '0.85rem', color: colors.text, marginTop: '0.35rem'}}>
                    {client.agent ? `${client.agent.first_name} ${client.agent.last_name}` : 'No agent'}
                  </div>
                  <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem', flexWrap: 'wrap'}}>
                    <span style={{
                      ...styles.badge,
                      background: client.status === 'active' ? '#10b981' : client.status === 'invited' ? '#f59e0b' : '#6b7280'
                    }}>{client.status}</span>
                    <span style={{fontSize: '0.8rem', color: colors.textMuted}}>
                      {client.property_type?.replace('_', ' ') || 'N/A'} · {new Date(client.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
          <div style={{...styles.tableContainer, background: colors.surface, border: `1px solid ${colors.border}`}}>
            <table style={{width: '100%'}}>
              <thead>
                <tr style={{borderBottom: `2px solid ${colors.border}`}}>
                  <th style={{...styles.th, color: colors.text}}>Client Name</th>
                  <th style={{...styles.th, color: colors.text}}>Email</th>
                  <th style={{...styles.th, color: colors.text}}>Agent</th>
                  <th style={{...styles.th, color: colors.text}}>Status</th>
                  <th style={{...styles.th, color: colors.text}}>Property Type</th>
                  <th style={{...styles.th, color: colors.text}}>Added</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map(client => (
                  <tr key={client.id} style={{borderBottom: `1px solid ${colors.borderLight}`}}>
                    <td style={{...styles.td, color: colors.text, fontWeight: '600'}}>
                      {client.first_name} {client.last_name}
                    </td>
                    <td style={{...styles.td, color: colors.textSecondary}}>{client.email}</td>
                    <td style={{...styles.td, color: colors.text}}>
                      {client.agent ? (
                        <div>
                          <div style={{fontWeight: '500'}}>{client.agent.first_name} {client.agent.last_name}</div>
                          <div style={{fontSize: '0.8rem', color: colors.textMuted}}>{client.agent.email}</div>
                        </div>
                      ) : (
                        <span style={{color: colors.textMuted}}>No agent</span>
                      )}
                    </td>
                    <td style={{...styles.td}}>
                      <span style={{
                        ...styles.badge,
                        background: client.status === 'active' ? '#10b981' :
                                   client.status === 'invited' ? '#f59e0b' : '#6b7280'
                      }}>
                        {client.status}
                      </span>
                    </td>
                    <td style={{...styles.td, color: colors.textMuted}}>
                      {client.property_type?.replace('_', ' ') || 'N/A'}
                    </td>
                    <td style={{...styles.td, color: colors.textMuted, fontSize: '0.85rem'}}>
                      {new Date(client.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

      {/* Vendors Tab */}
      {activeTab === 'vendors' && (
        <div style={styles.content}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
            <h2 style={{...styles.sectionTitle, color: colors.text, margin: 0}}>
              Regional Vendors ({filteredVendors.length})
            </h2>
          </div>
          <p style={{color: colors.textSecondary, marginTop: 0}}>
            Pacarion does not add regional vendors from applications. An accepted application is added only to the inviting agent's own vendor list. You can correct or remove an existing regional listing.
          </p>

          {showVendorForm && editingVendorId && (
            <div style={{...styles.formCard, background: colors.surface, border: `1px solid ${colors.border}`, marginBottom: '2rem'}}>
              <h3 style={{color: colors.text, marginBottom: '1rem'}}>Edit vendor</h3>
              <form onSubmit={handleSaveVendor}>
                <div style={{...styles.formRow, flexWrap: 'wrap'}}>
                  <div style={{flex: 1}}>
                    <label style={{...styles.label, color: colors.text}}>Vendor Name *</label>
                    <input
                      type="text"
                      value={vendorForm.name}
                      onChange={(e) => setVendorForm({...vendorForm, name: e.target.value})}
                      style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
                      required
                    />
                  </div>
                  <div style={{flex: '1 1 16rem', minWidth: 0}}>
                    <SearchableCategorySelect
                      id="admin-vendor-category"
                      label="Category"
                      value={vendorForm.category}
                      onChange={(value) => setVendorForm({...vendorForm, category: value})}
                      options={vendorCategoryOptions(vendors.concat([{ category: vendorForm.category }]))}
                      allowEmpty={false}
                      required
                      startOpen={Boolean(seeded && seeded.formCategoryOpen)}
                      colors={colors}
                    />
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={{flex: 1}}>
                    <label style={{...styles.label, color: colors.text}}>Phone</label>
                    <input
                      type="tel"
                      value={vendorForm.phone}
                      onChange={(e) => setVendorForm({...vendorForm, phone: e.target.value})}
                      style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
                      placeholder="(407) 555-1234"
                    />
                  </div>
                  <div style={{flex: 1}}>
                    <label style={{...styles.label, color: colors.text}}>Email</label>
                    <input
                      type="email"
                      value={vendorForm.email}
                      onChange={(e) => setVendorForm({...vendorForm, email: e.target.value})}
                      style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
                      placeholder="vendor@example.com"
                    />
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={{flex: 1}}>
                    <label style={{...styles.label, color: colors.text}}>Website</label>
                    <input
                      type="url"
                      value={vendorForm.website}
                      onChange={(e) => setVendorForm({...vendorForm, website: e.target.value})}
                      style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
                      placeholder="www.vendor.com"
                    />
                  </div>
                  <div style={{width: '200px'}}>
                    <label style={{...styles.label, color: colors.text}}>Region *</label>
                    <select
                      value={vendorForm.region}
                      onChange={(e) => setVendorForm({...vendorForm, region: e.target.value})}
                      style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
                      required
                    >
                      <option value="North Orlando">North Orlando</option>
                      <option value="South Orlando">South Orlando</option>
                      <option value="East Orlando">East Orlando</option>
                      <option value="West Orlando">West Orlando</option>
                      <option value="Central Orlando">Central Orlando</option>
                    </select>
                  </div>
                </div>

                <div style={{marginBottom: '1rem'}}>
                  <label style={{...styles.label, color: colors.text}}>Address</label>
                  <input
                    type="text"
                    value={vendorForm.address}
                    onChange={(e) => setVendorForm({...vendorForm, address: e.target.value})}
                    style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
                    placeholder="123 Main St, Orlando, FL 32801"
                  />
                </div>

                <div style={{marginBottom: '1rem'}}>
                  <label style={{...styles.label, color: colors.text}}>Description</label>
                  <textarea
                    value={vendorForm.description}
                    onChange={(e) => setVendorForm({...vendorForm, description: e.target.value})}
                    style={{
                      ...styles.input,
                      background: colors.surface,
                      border: `2px solid ${colors.borderLight}`,
                      color: colors.text,
                      minHeight: '80px',
                      resize: 'vertical'
                    }}
                    placeholder="Brief description of services..."
                  />
                </div>

                <div style={styles.formRow}>
                  <div style={{flex: 1}}>
                    <label style={{...styles.label, color: colors.text}}>License Number</label>
                    <input
                      type="text"
                      value={vendorForm.license_number}
                      onChange={(e) => setVendorForm({...vendorForm, license_number: e.target.value})}
                      style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
                    />
                  </div>
                  <div style={{flex: 1}}>
                    <label style={{...styles.label, color: colors.text}}>Rating (0.0 - 5.0)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="5"
                      value={vendorForm.rating || ''}
                      onChange={(e) => setVendorForm({...vendorForm, rating: e.target.value ? parseFloat(e.target.value) : null})}
                      style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
                      placeholder="4.5"
                    />
                  </div>
                </div>

                <div style={{marginBottom: '1rem'}}>
                  <label style={{...styles.label, color: colors.text}}>Internal Notes (only visible to Super Admin)</label>
                  <textarea
                    value={vendorForm.notes}
                    onChange={(e) => setVendorForm({...vendorForm, notes: e.target.value})}
                    style={{
                      ...styles.input,
                      background: colors.surface,
                      border: `2px solid ${colors.borderLight}`,
                      color: colors.text,
                      minHeight: '60px',
                      resize: 'vertical'
                    }}
                    placeholder="Private notes about this vendor..."
                  />
                </div>

                <div style={{display: 'flex', gap: '1rem', marginBottom: '1rem'}}>
                  <label style={{...styles.label, color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <input
                      type="checkbox"
                      checked={vendorForm.is_recommended}
                      onChange={(e) => setVendorForm({...vendorForm, is_recommended: e.target.checked})}
                    />
                    Recommended Vendor
                  </label>
                </div>

                <div style={{display: 'flex', gap: '0.75rem'}}>
                  <button type="submit" style={{...styles.actionBtn, padding: '0.75rem 2rem'}}>
                    Save changes
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowVendorForm(false); setEditingVendorId(null); }}
                    style={{...styles.actionBtn, padding: '0.75rem 1.5rem', background: '#6b7280'}}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Filters */}
          <div style={{display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap'}}>
            <input
              type="text"
              placeholder="Search vendors by name..."
              value={vendorSearch}
              onChange={(e) => setVendorSearch(e.target.value)}
              style={{
                ...styles.searchInput,
                flex: 1,
                minWidth: '250px',
                background: colors.surface,
                border: `2px solid ${colors.borderLight}`,
                color: colors.text,
                marginBottom: 0
              }}
            />
            <select
              value={vendorRegionFilter}
              onChange={(e) => setVendorRegionFilter(e.target.value)}
              style={{
                ...styles.searchInput,
                width: '200px',
                background: colors.surface,
                border: `2px solid ${colors.borderLight}`,
                color: colors.text,
                marginBottom: 0
              }}
            >
              <option value="">All Regions</option>
              <option value="North Orlando">North Orlando</option>
              <option value="South Orlando">South Orlando</option>
              <option value="East Orlando">East Orlando</option>
              <option value="West Orlando">West Orlando</option>
              <option value="Central Orlando">Central Orlando</option>
            </select>
            <SearchableCategorySelect
              id="admin-vendor-category-filter"
              label="Category"
              value={vendorCategoryFilter}
              onChange={setVendorCategoryFilter}
              options={vendorCategoryOptions(vendors)}
              emptyLabel="All categories"
              compact
              startOpen={Boolean(seeded && seeded.categoryOpen)}
              colors={colors}
            />
          </div>

          <VendorCardFootnote agentName="" style={{ color: colors.textSecondary }} />
          {/* Vendor Cards */}
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem'}}>
            {filteredVendors.map(vendor => (
              <div
                key={vendor.id}
                style={{
                  ...styles.formCard,
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  opacity: vendor.is_active ? 1 : 0.6,
                  position: 'relative'
                }}
              >
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem'}}>
                  <div style={{flex: 1}}>
                    <h3 style={{color: colors.text, margin: '0 0 0.5rem 0', fontSize: '1.2rem'}}>
                      {vendor.name}
                    </h3>
                    <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem'}}>
                      <span style={{...styles.badge, background: '#59c4c4', fontSize: '0.75rem'}}>
                        {vendor.category}
                      </span>
                      <span style={{...styles.badge, background: '#8b5cf6', fontSize: '0.75rem'}}>
                        {vendor.region}
                      </span>
                      {vendor.is_recommended && (
                        <span style={{...styles.badge, background: '#f59e0b', fontSize: '0.75rem'}}>
                          Recommended
                        </span>
                      )}
                      {!vendor.is_active && (
                        <span style={{...styles.badge, background: '#dc2626', fontSize: '0.75rem'}}>
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <VendorCardCopy
                  description={vendor.description}
                  color={colors.textSecondary}
                  empty="No description provided"
                  marginBottom="1rem"
                />

                <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem', fontSize: '0.9rem'}}>
                  {vendor.phone && (
                    <div style={{color: colors.text}}>
                       <a
                        href={`tel:${vendor.phone}`}
                        onClick={(event) => hireGate.requestContact(event, {
                          href: event.currentTarget.href,
                          vendorId: vendor.id,
                          kind: 'phone',
                        })}
                        style={{color: colors.text, textDecoration: 'none'}}
                      >
                        {vendor.phone}
                      </a>
                    </div>
                  )}
                  {vendor.email && (
                    <div style={{color: colors.text}}>
                       <a
                        href={`mailto:${vendor.email}`}
                        onClick={(event) => hireGate.requestContact(event, {
                          href: event.currentTarget.href,
                          vendorId: vendor.id,
                          kind: 'email',
                        })}
                        style={{color: colors.text, textDecoration: 'none'}}
                      >
                        {vendor.email}
                      </a>
                    </div>
                  )}
                  {vendor.website && (
                    <div style={{color: colors.text}}>
                       <a
                        href={`https://${vendor.website.replace(/^https?:\/\//, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => hireGate.requestContact(event, {
                          href: event.currentTarget.href,
                          newTab: true,
                          vendorId: vendor.id,
                          kind: 'website',
                        })}
                        style={{color: '#59c4c4'}}
                      >
                        {vendor.website}
                      </a>
                    </div>
                  )}
                  {vendor.address && (
                    <div style={{color: colors.textSecondary, fontSize: '0.85rem'}}>
                       {vendor.address}
                    </div>
                  )}
                  {vendor.rating && (
                    <div style={{color: colors.text, fontWeight: '600'}}>
                      {vendor.rating} / 5.0
                    </div>
                  )}
                  {vendor.license_number && (
                    <div style={{color: colors.textMuted, fontSize: '0.85rem'}}>
                      License: {vendor.license_number}
                    </div>
                  )}
                </div>

                {vendor.notes && (
                  <div style={{
                    background: colors.surfaceAlt,
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    marginBottom: '1rem',
                    fontSize: '0.85rem'
                  }}>
                    <div style={{color: colors.textMuted, marginBottom: '0.25rem', fontWeight: '600'}}>
                       Internal Notes:
                    </div>
                    <div style={{color: colors.textSecondary}}>
                      {vendor.notes}
                    </div>
                  </div>
                )}

                <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
                  <button
                    type="button"
                    onClick={() => startEditVendor(vendor)}
                    style={{
                      ...styles.actionBtn,
                      background: '#59c4c4',
                      fontSize: '0.8rem',
                      padding: '0.5rem 0.75rem'
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleToggleVendorActive(vendor)}
                    style={{
                      ...styles.actionBtn,
                      background: vendor.is_active ? '#6b7280' : '#10b981',
                      fontSize: '0.8rem',
                      padding: '0.5rem 0.75rem'
                    }}
                  >
                    {vendor.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => handleDeleteVendor(vendor.id)}
                    style={{
                      ...styles.actionBtn,
                      background: '#dc2626',
                      fontSize: '0.8rem',
                      padding: '0.5rem 0.75rem'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredVendors.length === 0 && (
            <div style={{textAlign: 'center', padding: '3rem', color: colors.textSecondary}}>
              <p>No vendors found matching your filters.</p>
            </div>
          )}
        </div>
      )}

      {/* Activity Log Tab */}
      {activeTab === 'tasks' && (
        <div style={styles.content}>
          <GlobalTasksAdmin
            colors={colors}
            narrow={narrow}
            logAdminAction={seeded ? null : logAdminAction}
            previewTasks={seeded ? (seeded.globalTasks || []) : undefined}
            previewTemplates={seeded ? (seeded.globalTemplates || []) : undefined}
            previewCounts={seeded ? (seeded.globalTaskCounts || {}) : undefined}
            startMode={seeded && seeded.taskMode ? seeded.taskMode : ''}
            startTaskId={seeded && seeded.startTaskId ? seeded.startTaskId : ''}
          />
        </div>
      )}

      {activeTab === 'activity' && (
        <div style={styles.content}>
          <h2 style={{...styles.sectionTitle, color: colors.text}}>Admin Activity Log</h2>

          <div style={{display: 'flex', gap: '1rem', marginBottom: '1rem', flexDirection: narrow ? 'column' : 'row'}}>
            <input
              type="text"
              placeholder="Filter by action type..."
              value={logActionFilter}
              onChange={(e) => setLogActionFilter(e.target.value)}
              style={{
                ...styles.searchInput,
                flex: 1,
                background: colors.surface,
                border: `2px solid ${colors.borderLight}`,
                color: colors.text,
                marginBottom: 0
              }}
            />
            <input
              type="text"
              placeholder="Filter by date..."
              value={logDateFilter}
              onChange={(e) => setLogDateFilter(e.target.value)}
              style={{
                ...styles.searchInput,
                width: narrow ? '100%' : '200px',
                background: colors.surface,
                border: `2px solid ${colors.borderLight}`,
                color: colors.text,
                marginBottom: 0
              }}
            />
          </div>

          <div style={styles.logList}>
            {filteredActivityLogs.map(log => (
              <div key={log.id} style={{...styles.logItem, background: colors.surface, border: `1px solid ${colors.border}`}}>
                <div style={styles.logHeader}>
                  <span style={{...styles.logAction, color: colors.text}}>{activitySentence(log)}</span>
                  <span style={{...styles.logTime, color: colors.textMuted}}>
                    {new Date(log.created_at).toLocaleString()}
                  </span>
                </div>
                {log.admin?.email && (
                  <div style={{...styles.logDetails, color: colors.textSecondary}}>
                    {log.admin.email}
                  </div>
                )}
              </div>
            ))}
          </div>

          {filteredActivityLogs.length === 0 && (
            <div style={{textAlign: 'center', padding: '3rem', color: colors.textSecondary}}>
              <p>No activity logs match your filters.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'discounts' && (
        <div style={styles.content}>
          <DiscountCodesPanel
            colors={colors}
            narrow={narrow}
            preview={Boolean(seeded)}
            initialCodes={(seeded && seeded.discounts) || []}
            initialMode={seeded && seeded.discountMode}
            homeownerOpen={seeded ? seeded.homeownerOpen : undefined}
            adminId={profile && profile.id}
          />
        </div>
      )}

      {activeTab === 'applications' && (
        <div style={styles.content}>
          <h2 style={{...styles.sectionTitle, color: colors.text}}>Applications</h2>
          <div style={{...styles.tabs, marginBottom: '1rem'}}>
            {APPLICATION_FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                style={{...styles.tab, ...(applicationFilter === filter.id && styles.tabActive)}}
                onClick={() => setApplicationFilter(filter.id)}
              >
                {filter.label} ({applicationsForFilter(applications, filter.id).length})
              </button>
            ))}
          </div>
          <p style={{color: colors.textSecondary, marginTop: 0}}>{applicationFilterSubtitle(applicationFilter)}</p>
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
            {applicationsForFilter(applications, applicationFilter).map((application) => {
              const sponsor = application.sponsor
                ? [application.sponsor.first_name, application.sponsor.last_name].filter(Boolean).join(' ')
                : '';
              return (
                <div
                  key={application.id}
                  style={{...styles.formCard, background: colors.surface, border: `1px solid ${colors.border}`}}
                >
                  <div style={{display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap'}}>
                    <div style={{fontWeight: '650', color: colors.text}}>{applicationCard(application).title}</div>
                    <span style={{...styles.badge, background: application.status === 'approved' ? '#0f766e' : application.status === 'declined' ? '#64748b' : '#59c4c4', color: application.status === 'approved' || application.status === 'declined' ? '#fff' : '#083344'}}>
                      {applicationStatusLabel(application.status)}
                    </span>
                  </div>
                  {applicationCard(application).trades ? (
                    <div style={{color: colors.textSecondary, marginTop: '0.35rem'}}>
                      {applicationCard(application).trades}
                    </div>
                  ) : null}
                  {applicationCard(application).email ? (
                    <div style={{color: colors.text, marginTop: '0.35rem'}}>{applicationCard(application).email}</div>
                  ) : null}
                  {application.phone && <div style={{color: colors.textSecondary}}>{application.phone}</div>}
                  {sponsor && <div style={{color: colors.textMuted, fontSize: '0.85rem', marginTop: '0.35rem'}}>Invited by {sponsor}</div>}
                  <div style={{color: colors.textMuted, fontSize: '0.85rem', marginTop: '0.35rem'}}>
                    {new Date(application.submitted_at || application.created_at).toLocaleDateString()}
                  </div>
                </div>
              );
            })}
          </div>
          {applicationsForFilter(applications, applicationFilter).length === 0 && (
            <div style={{textAlign: 'center', padding: '3rem', color: colors.textSecondary}}>
              <p>{applications.length === 0 ? 'No vendor applications yet.' : 'None in this status.'}</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'inquiries' && (
        <div style={styles.content}>
          <h2 style={{...styles.sectionTitle, color: colors.text}}>Inquiries</h2>
          <p style={{color: colors.textSecondary, marginTop: 0}}>Companies asking about an enterprise plan.</p>
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
            {inquiries.map((inquiry) => (
              <div
                key={inquiry.id}
                style={{...styles.formCard, background: colors.surface, border: `1px solid ${colors.border}`}}
              >
                <div style={{display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap'}}>
                  <div style={{fontWeight: '700', color: colors.text}}>{inquiry.company}</div>
                  <span style={{...styles.badge, background: '#59c4c4'}}>{inquiryStatusLabel(inquiry.status)}</span>
                </div>
                <div style={{color: colors.text, marginTop: '0.35rem'}}>{inquiry.first_name} {inquiry.last_name}</div>
                <div style={{color: colors.textSecondary}}>{inquiry.email}</div>
                {inquiry.phone && <div style={{color: colors.textSecondary}}>{inquiry.phone}</div>}
                <div style={{color: colors.text, marginTop: '0.35rem'}}>{inquiry.agent_count} agents</div>
                {inquiry.message && (
                  <div style={{color: colors.textSecondary, marginTop: '0.5rem'}}>{inquiry.message}</div>
                )}
                <div style={{color: colors.textMuted, fontSize: '0.85rem', marginTop: '0.35rem'}}>
                  {new Date(inquiry.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
          {inquiries.length === 0 && (
            <div style={{textAlign: 'center', padding: '3rem', color: colors.textSecondary}}>
              <p>No enterprise inquiries yet.</p>
            </div>
          )}
        </div>
      )}

      {CEO_SEAT_TABS.some((tab) => tab.id === activeTab) && (
        <div style={styles.content}>
          <CeoSeat tab={activeTab} colors={colors} narrow={narrow} />
        </div>
      )}

      {/* Agent Edit Form Modal */}
      {showAgentForm && editingAgent && (
        <div style={styles.modal} onClick={() => { setShowAgentForm(false); setEditingAgent(null); }}>
          <div
            style={{...styles.modalContent, background: colors.surface, border: `1px solid ${colors.border}`}}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{color: colors.text, marginBottom: '1.5rem'}}>
              Edit Agent: {editingAgent.first_name} {editingAgent.last_name}
            </h2>

            <form onSubmit={handleSaveAgent}>
              <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                {/* First Name */}
                <div>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: colors.text}}>
                    First Name
                  </label>
                  <input
                    type="text"
                    value={agentForm.first_name}
                    onChange={(e) => setAgentForm({...agentForm, first_name: e.target.value})}
                    style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                    required
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: colors.text}}>
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={agentForm.last_name}
                    onChange={(e) => setAgentForm({...agentForm, last_name: e.target.value})}
                    style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: colors.text}}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={agentForm.email}
                    onChange={(e) => setAgentForm({...agentForm, email: e.target.value})}
                    style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                    required
                  />
                </div>

                {/* Phone */}
                <div>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: colors.text}}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={agentForm.phone}
                    onChange={(e) => setAgentForm({...agentForm, phone: e.target.value})}
                    style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                  />
                </div>

                {/* Company Name */}
                <div>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: colors.text}}>
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={agentForm.company_name}
                    onChange={(e) => setAgentForm({...agentForm, company_name: e.target.value})}
                    style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                  />
                </div>

                {/* Subscription Plan */}
                <div>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: colors.text}}>
                    Subscription Plan
                  </label>
                  <select
                    value={agentForm.subscription_plan}
                    onChange={(e) => setAgentForm({...agentForm, subscription_plan: e.target.value})}
                    style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                  >
                    <option value="solo">Solo</option>
                    <option value="team">Team</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>

                {/* Subscription Status */}
                <div>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: colors.text}}>
                    Subscription Status
                  </label>
                  <select
                    value={agentForm.subscription_status}
                    onChange={(e) => setAgentForm({...agentForm, subscription_status: e.target.value})}
                    style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                  >
                    <option value="active">Active</option>
                    <option value="trialing">Trialing</option>
                    <option value="past_due">Past Due</option>
                    <option value="canceled">Canceled</option>
                    <option value="paused">Paused</option>
                  </select>
                </div>
              </div>

              {/* Form Actions */}
              <div style={styles.modalActions}>
                <button
                  type="submit"
                  style={{...styles.actionBtn, background: '#10b981', flex: 1}}
                >
                  Save changes
                </button>
                <button
                  type="button"
                  onClick={() => { setShowAgentForm(false); setEditingAgent(null); }}
                  style={{...styles.actionBtn, background: '#6b7280', flex: 1}}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Agent Details Modal */}
      {selectedAgent && (
        <div style={styles.modal} onClick={() => setSelectedAgent(null)}>
          <div
            style={{...styles.modalContent, background: colors.surface, border: `1px solid ${colors.border}`}}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{color: colors.text}}>
              {selectedAgent.first_name} {selectedAgent.last_name}
            </h2>
            <p style={{color: colors.textSecondary}}>{selectedAgent.email}</p>

            <div style={{marginTop: '1.5rem'}}>
              <h3 style={{color: colors.text, marginBottom: '0.75rem'}}>Subscription Details</h3>
              <div style={{...styles.infoGrid, background: colors.surfaceAlt, padding: '1rem', borderRadius: '0.5rem'}}>
                <div>
                  <div style={{color: colors.textMuted, fontSize: '0.85rem'}}>Plan</div>
                  <div style={{color: colors.text, fontWeight: '600'}}>{selectedAgent.subscription_plan || 'None'}</div>
                </div>
                <div>
                  <div style={{color: colors.textMuted, fontSize: '0.85rem'}}>Status</div>
                  <div style={{color: colors.text, fontWeight: '600'}}>{selectedAgent.subscription_status || 'None'}</div>
                </div>
                <div>
                  <div style={{color: colors.textMuted, fontSize: '0.85rem'}}>Clients</div>
                  <div style={{color: colors.text, fontWeight: '600'}}>{selectedAgent.client_count}</div>
                </div>
                <div>
                  <div style={{color: colors.textMuted, fontSize: '0.85rem'}}>Joined</div>
                  <div style={{color: colors.text, fontWeight: '600'}}>
                    {new Date(selectedAgent.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.modalActions}>
              <button
                onClick={() => {
                  if (window.confirm('Extend trial by 30 days?')) {
                    const newTrialEnd = new Date();
                    newTrialEnd.setDate(newTrialEnd.getDate() + 30);
                    handleUpdateSubscription(selectedAgent.id, {
                      trial_ends_at: newTrialEnd.toISOString()
                    });
                  }
                }}
                style={{...styles.actionBtn, background: '#10b981'}}
              >
                Extend Trial (30 days)
              </button>
              <button
                onClick={() => setSelectedAgent(null)}
                style={{...styles.actionBtn, background: '#6b7280'}}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <BeforeYouHireModal
        open={!!hireGate.pending}
        saving={hireGate.saving}
        error={hireGate.error}
        onAccept={hireGate.accept}
        onDismiss={hireGate.dismiss}
      />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    maxWidth: '1100px',
    margin: '0 auto',
    boxSizing: 'border-box'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.25rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid #e5e7eb',
    gap: '0.75rem'
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 600,
    letterSpacing: '-0.03em',
    margin: 0,
    marginBottom: '0.15rem'
  },
  subtitle: {
    fontSize: '0.95rem',
    margin: 0
  },
  headerActions: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center'
  },
  adminBadge: {
    padding: '0.35rem 0.7rem',
    borderRadius: '999px',
    fontWeight: 600,
    fontSize: '0.75rem',
    letterSpacing: '0.01em',
    background: 'transparent'
  },
  logoutBtn: {
    padding: '0.4rem 0.75rem',
    background: 'transparent',
    borderRadius: '999px',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.9rem'
  },
  tabBar: {
    display: 'flex',
    gap: '0.4rem',
    marginBottom: '1.25rem',
    overflowX: 'auto',
    flexWrap: 'nowrap',
    WebkitOverflowScrolling: 'touch',
    paddingBottom: '0.25rem'
  },
  tabs: {
    display: 'flex',
    gap: '0.4rem',
    marginBottom: '1rem',
    overflowX: 'auto',
    flexWrap: 'nowrap'
  },
  tab: {
    flex: '0 0 auto',
    background: 'transparent',
    border: '1px solid #e5e7eb',
    borderRadius: '999px',
    padding: '0.45rem 0.85rem',
    fontSize: '0.92rem',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },
  tabActive: {
    color: '#ffffff',
    background: '#0f172a',
    borderColor: '#0f172a'
  },
  content: {
    marginTop: '1rem'
  },
  sectionTitle: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
    fontWeight: '600'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '1.25rem',
    marginTop: '1rem'
  },
  statCard: {
    padding: '1.5rem',
    borderRadius: '1rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
  },
  statValue: {
    fontSize: '2.25rem',
    fontWeight: 'bold',
    marginBottom: '0.25rem'
  },
  statLabel: {
    fontSize: '0.95rem',
    marginBottom: '0.25rem',
    fontWeight: '500'
  },
  statSubtext: {
    fontSize: '0.85rem',
    marginTop: '0.5rem'
  },
  searchInput: {
    width: '100%',
    padding: '0.75rem 1rem',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    marginBottom: '1rem'
  },
  tableContainer: {
    borderRadius: '0.75rem',
    overflow: 'auto',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
  },
  th: {
    padding: '1rem',
    textAlign: 'left',
    fontWeight: '600',
    fontSize: '0.9rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  td: {
    padding: '1rem',
    fontSize: '0.95rem'
  },
  badge: {
    padding: '0.35rem 0.75rem',
    borderRadius: '0.5rem',
    color: 'white',
    fontSize: '0.8rem',
    fontWeight: '600',
    display: 'inline-block',
    textTransform: 'capitalize'
  },
  actionBtn: {
    padding: '0.5rem 1rem',
    background: '#59c4c4',
    color: 'white',
    border: 'none',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: '600',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap'
  },
  quickActionBtn: {
    padding: '1rem 1.5rem',
    color: 'white',
    border: 'none',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'all 0.2s'
  },
  logList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  logItem: {
    padding: '1.25rem',
    borderRadius: '0.75rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
  },
  logHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0.75rem',
    alignItems: 'flex-start',
    gap: '0.75rem',
    flexWrap: 'wrap'
  },
  logAction: {
    fontWeight: '600',
    fontSize: '1rem'
  },
  logTime: {
    fontSize: '0.85rem',
    whiteSpace: 'nowrap'
  },
  logDetails: {
    fontSize: '0.9rem',
    marginBottom: '0.5rem'
  },
  logJson: {
    marginTop: '0.75rem',
    padding: '0.75rem',
    borderRadius: '0.5rem',
    fontSize: '0.75rem',
    overflow: 'auto',
    maxHeight: '200px'
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem'
  },
  modalContent: {
    padding: '2rem',
    borderRadius: '1rem',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto'
  },
  modalActions: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '2rem',
    flexWrap: 'wrap'
  },
  formCard: {
    padding: '1.5rem',
    borderRadius: '1rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
  },
  formRow: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1rem'
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '500',
    fontSize: '0.9rem'
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    boxSizing: 'border-box'
  },
  templateCard: {
    padding: '1.5rem',
    borderRadius: '0.75rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    transition: 'all 0.2s'
  },
  templateMeta: {
    padding: '0.35rem 0.75rem',
    borderRadius: '0.5rem',
    fontSize: '0.85rem',
    fontWeight: '500'
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '1rem'
  },
  loader: {
    border: '4px solid #f3f4f6',
    borderRadius: '50%',
    borderTop: '4px solid #59c4c4',
    width: '40px',
    height: '40px',
    animation: 'spin 1s linear infinite',
    margin: '0 auto'
  }
};

// Add CSS animation. A test environment may not have a stylesheet yet.
if (typeof document !== 'undefined') {
  let styleSheet = document.styleSheets && document.styleSheets[0];
  if (!styleSheet && document.head) {
    const style = document.createElement('style');
    document.head.appendChild(style);
    styleSheet = style.sheet || (document.styleSheets && document.styleSheets[0]);
  }
  if (styleSheet) {
    try {
      styleSheet.insertRule(`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `, styleSheet.cssRules.length);
    } catch (error) {
      // The rule is already present.
    }
  }
}

export default SuperAdminDashboard;
